import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCity } from "@/contexts/Citycontext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  LogOut, User as UserIcon, LayoutDashboard, Bell,
  Home, Briefcase, Building2, Mail, MapPin, ChevronDown, X, Search as SearchIcon
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import logo from "../assets/jd_logo.png";
import ScrollingText from "./ScrollingText";
import InstallAppButton from "./InstallAppButton";
import NotificationBell from "./NotificationBell";

const dashboardPath = (role) => {
  if (role === "job_poster") return "/dashboard/poster";
  if (role === "job_seeker") return "/dashboard/seeker";
  if (role === "admin") return "/admin";
  if (role === "super_admin") return "/superadmin";
  return "/";
};

export default function Navbar() {
  const { user, logout } = useAuth();
  const { selectedCity, setSelectedCity } = useCity();
  const navigate = useNavigate();
  const location = useLocation();
  const [cities, setCities] = useState([]);
  const [mobileCityOpen, setMobileCityOpen] = useState(false);
  const [citySearch, setCitySearch] = useState("");
  const citySearchInputRef = useRef(null);

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    api.get("/cities").then(r => setCities(r.data)).catch(() => {});
  }, []);

  const cityQuery = citySearch.trim().toLowerCase();
  const filteredCities = (cityQuery
    ? cities.filter(c => c.name.toLowerCase().includes(cityQuery))
    : cities
  ).slice().sort((a, b) => a.name.localeCompare(b.name));

  const handleCitySelect = (cityName) => {
    setSelectedCity(cityName);
    setMobileCityOpen(false);
    setCitySearch("");
  };

  const clearCity = (e) => {
    e.stopPropagation();
    setSelectedCity("");
  };

  return (
    <>
      {/* ── TOP NAVBAR ── */}
      <nav
        className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-md border-b border-slate-200"
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex h-14 md:h-16 items-center justify-between gap-2">

            {/* ── Logo ── */}
            {/* Mobile: compact logo (icon only width), Desktop: full width */}
            <Link
              to="/"
              className="flex items-center shrink-0"
              data-testid="logo-link"
            >
              {/* Desktop logo — full size */}
              <img
                src={logo}
                className="hidden md:block h-16 w-44 object-contain"
                alt="logo"
              />
              {/* Mobile logo — tighter crop so it doesn't overflow */}
              <img
                src={logo}
                className="md:hidden h-10 w-28 object-contain object-left"
                alt="logo"
              />
            </Link>

            {/* ── DESKTOP: Center nav links ── */}
            <div className="hidden md:flex items-center gap-1">
              <Link to="/jobs"            className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-black" data-testid="nav-jobs">Browse Jobs</Link>
              <Link to="/jobs?featured=1" className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-black" data-testid="nav-featured">Featured</Link>
              <Link to="/about"           className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-black" data-testid="nav-about">About</Link>
              <Link to="/contact"         className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-black" data-testid="nav-contact">Contact</Link>
            </div>

            {/* ── DESKTOP: City Picker + Auth ── */}
            <div className="hidden md:flex items-center gap-3">
              <InstallAppButton className="h-9 py-0" />
              <NotificationBell />
              {/* City Dropdown */}
              <DropdownMenu
                onOpenChange={(open) => {
                  setCitySearch("");
                  if (open) setTimeout(() => citySearchInputRef.current?.focus(), 10);
                }}
              >
                <DropdownMenuTrigger asChild>
                  {/* Fixed width so this pill never resizes the navbar —
                      selecting/clearing a city must not shift the nav links */}
                  <Button
                    variant="outline"
                    size="sm"
                    className={`flex items-center gap-1.5 h-9 px-3 w-[150px] border rounded-lg text-sm font-medium transition-colors
                      ${selectedCity
                        ? "border-black bg-black text-white hover:bg-slate-800 hover:text-white"
                        : "border-slate-300 text-slate-600 hover:border-black hover:text-black bg-white"
                      }`}
                    data-testid="city-picker-btn"
                  >
                    <MapPin className="w-3.5 h-3.5 shrink-0" />
                    <ScrollingText text={selectedCity || "All Cities"} widthClass="w-[84px]" />
                    {selectedCity ? (
                      <span
                        role="button"
                        onClick={clearCity}
                        onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                        className="ml-0.5 shrink-0 hover:opacity-70 rounded-full"
                        data-testid="clear-city-btn"
                      >
                        <X className="w-3 h-3" />
                      </span>
                    ) : (
                      <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-60" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-56"
                  onCloseAutoFocus={(e) => e.preventDefault()}
                >
                  <DropdownMenuLabel className="text-xs text-slate-500 font-normal">Select your city</DropdownMenuLabel>
                  <div className="px-2 pb-1.5">
                    <div className="relative">
                      <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-black pointer-events-none" />
                      <input
                        ref={citySearchInputRef}
                        value={citySearch}
                        onChange={e => setCitySearch(e.target.value)}
                        onKeyDown={e => e.stopPropagation()}
                        placeholder="Search city..."
                        data-testid="city-dropdown-search"
                        className="w-full pl-6 pr-2 py-1.5 text-xs rounded-md border border-slate-200 focus:outline-none focus:ring-1 focus:ring-black"
                      />
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => handleCitySelect("")}
                    className={!selectedCity ? "font-semibold text-black" : "text-slate-600"}
                    data-testid="city-option-all"
                  >
                    <MapPin className="w-3.5 h-3.5 mr-2 shrink-0 opacity-50" />
                    All Cities
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <div className="max-h-56 overflow-y-auto">
                    {filteredCities.length === 0 ? (
                      <p className="px-3 py-3 text-xs text-slate-400">No matching cities</p>
                    ) : filteredCities.map(c => (
                      <DropdownMenuItem
                        key={c.id}
                        onClick={() => handleCitySelect(c.name)}
                        className={`flex items-center ${selectedCity === c.name ? "font-semibold text-black bg-slate-50" : "text-slate-700"}`}
                        data-testid={`city-option-${c.name}`}
                      >
                        <MapPin className="w-3.5 h-3.5 mr-2 shrink-0 opacity-40" />
                        <ScrollingText text={c.name} widthClass="w-[120px]" />
                        {c.job_count > 0 && (
                          <span className="ml-auto pl-2 text-xs text-slate-400 shrink-0">{c.job_count}</span>
                        )}
                      </DropdownMenuItem>
                    ))}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Auth */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="rounded-full" data-testid="user-menu-btn">
                      <UserIcon className="w-4 h-4 mr-2" />
                      {user.name?.split(" ")[0]}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate(dashboardPath(user.role))} data-testid="nav-dashboard">
                      <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/notifications")} data-testid="nav-notifications">
                      <Bell className="w-4 h-4 mr-2" /> Notifications
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="logout-btn">
                      <LogOut className="w-4 h-4 mr-2" /> Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Link to="/login"><Button variant="ghost" data-testid="login-btn">Sign in</Button></Link>
                  <Link to="/register"><Button className="bg-black hover:bg-slate-800 text-white" data-testid="register-btn">Sign Up</Button></Link>
                </>
              )}
            </div>

            {/* ════════════════════════════════════════
                MOBILE RIGHT SIDE
                Logo (left) ── [City pill] [User/CTA] (right)
                ════════════════════════════════════════ */}
            <div className="md:hidden flex items-center gap-1.5 shrink-0">

              <InstallAppButton iconOnly />
              <NotificationBell iconOnly />

              {/* City pill — compact, fixed width so it never resizes */}
              <button
                type="button"
                onClick={() => setMobileCityOpen(true)}
                className={`flex items-center gap-1 h-8 px-2 rounded-lg border text-xs font-medium transition-colors w-[90px]
                  ${selectedCity
                    ? "border-black bg-black text-white"
                    : "border-slate-300 text-slate-600 bg-white"
                  }`}
                data-testid="city-picker-btn-mobile"
              >
                <MapPin className="w-3 h-3 shrink-0" />
                <ScrollingText text={selectedCity || "City"} widthClass="w-[46px]" />
                {selectedCity ? (
                  <span
                    role="button"
                    onClick={(e) => { e.stopPropagation(); setSelectedCity(""); }}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="shrink-0"
                  >
                    <X className="w-3 h-3 shrink-0" />
                  </span>
                ) : (
                  <ChevronDown className="w-3 h-3 shrink-0 opacity-60" />
                )}
              </button>

              {/* Auth — user menu or compact CTA */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="rounded-full border border-black/10 w-8 h-8"
                      data-testid="user-menu-btn-mobile"
                    >
                      <UserIcon className="w-4 h-4 text-black" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 border border-black/10">
                    <DropdownMenuLabel className="text-black text-xs truncate">{user.email}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate(dashboardPath(user.role))}>
                      <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/notifications")}>
                      <Bell className="w-4 h-4 mr-2" /> Notifications
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }}>
                      <LogOut className="w-4 h-4 mr-2" /> Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link to="/register">
                  <Button
                    size="sm"
                    className="bg-black hover:bg-slate-800 text-white text-xs px-3 h-8 rounded-lg whitespace-nowrap"
                    data-testid="register-btn-mobile"
                  >
                    Sign Up
                  </Button>
                </Link>
              )}
            </div>

          </div>
        </div>
      </nav>

      {/* ── MOBILE CITY SELECTOR SHEET ── */}
      {mobileCityOpen && (
        <div
          className="md:hidden fixed inset-0 z-[60] flex flex-col justify-end"
          style={{ background: "rgba(0,0,0,0.45)" }}
          onClick={() => { setMobileCityOpen(false); setCitySearch(""); }}
          data-testid="mobile-city-sheet"
        >
          <div
            className="bg-white rounded-t-3xl overflow-hidden shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Sheet handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-slate-200" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <div>
                <p className="text-sm font-semibold text-slate-900">Select City</p>
                <p className="text-xs text-slate-400 mt-0.5">Jobs will be filtered by city</p>
              </div>
              <button
                type="button"
                onClick={() => { setMobileCityOpen(false); setCitySearch(""); }}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Search */}
            <div className="px-4 pt-3">
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black pointer-events-none" />
                <input
                  value={citySearch}
                  onChange={e => setCitySearch(e.target.value)}
                  placeholder="Search city..."
                  data-testid="mobile-city-search"
                  className="w-full pl-9 pr-3 py-2.5 text-sm rounded-xl border border-slate-200 focus:outline-none focus:ring-1 focus:ring-black"
                />
              </div>
            </div>

            {/* City list */}
            <div className="overflow-y-auto max-h-[55vh] px-4 py-3 flex flex-col gap-1">
              {/* All Cities — hidden while actively searching, since it's not a match */}
              {!citySearch.trim() && (
                <button
                  type="button"
                  onClick={() => handleCitySelect("")}
                  className={`flex items-center justify-between w-full px-4 py-3 rounded-xl text-left transition-colors
                    ${!selectedCity ? "bg-black text-white" : "bg-slate-50 text-slate-700 hover:bg-slate-100"}`}
                  data-testid="mobile-city-option-all"
                >
                  <div className="flex items-center gap-2.5">
                    <MapPin className={`w-4 h-4 ${!selectedCity ? "text-white/70" : "text-slate-400"}`} />
                    <span className="text-sm font-medium">All Cities</span>
                  </div>
                  {!selectedCity && (
                    <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Selected</span>
                  )}
                </button>
              )}

              {filteredCities.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-slate-400">No matching cities</p>
              ) : filteredCities.map(c => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => handleCitySelect(c.name)}
                  className={`flex items-center justify-between w-full px-4 py-3 rounded-xl text-left transition-colors
                    ${selectedCity === c.name ? "bg-black text-white" : "bg-slate-50 text-slate-700 hover:bg-slate-100"}`}
                  data-testid={`mobile-city-option-${c.name}`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <MapPin className={`w-4 h-4 shrink-0 ${selectedCity === c.name ? "text-white/70" : "text-slate-400"}`} />
                    <ScrollingText text={c.name} widthClass="w-[180px] sm:w-[220px]" className="text-sm font-medium" />
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {c.job_count > 0 && (
                      <span className={`text-xs ${selectedCity === c.name ? "text-white/60" : "text-slate-400"}`}>
                        {c.job_count} jobs
                      </span>
                    )}
                    {selectedCity === c.name && (
                      <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Selected</span>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Safe area spacer */}
            <div className="h-6" />
          </div>
        </div>
      )}

      {/* ── MOBILE BOTTOM TAB BAR ── */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-black/10 flex items-center justify-around h-16 px-2">

        <Link to="/" className="flex flex-col items-center gap-0.5 flex-1" data-testid="tab-home">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${isActive("/") ? "bg-black" : ""}`}>
            <Home className={`w-5 h-5 ${isActive("/") ? "text-white" : "text-slate-400"}`} />
          </div>
          <span className={`text-[11px] font-medium ${isActive("/") ? "text-black" : "text-slate-400"}`}>Home</span>
        </Link>

        <Link to="/jobs" className="flex flex-col items-center gap-0.5 flex-1" data-testid="tab-jobs">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${isActive("/jobs") ? "bg-black" : ""}`}>
            <Briefcase className={`w-5 h-5 ${isActive("/jobs") ? "text-white" : "text-slate-400"}`} />
          </div>
          <span className={`text-[11px] font-medium ${isActive("/jobs") ? "text-black" : "text-slate-400"}`}>Jobs</span>
        </Link>

        <Link to="/about" className="flex flex-col items-center gap-0.5 flex-1" data-testid="tab-employers">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${isActive("/about") ? "bg-black" : ""}`}>
            <Building2 className={`w-5 h-5 ${isActive("/about") ? "text-white" : "text-slate-400"}`} />
          </div>
          <span className={`text-[11px] font-medium ${isActive("/about") ? "text-black" : "text-slate-400"}`}>About Us</span>
        </Link>

        <Link to="/contact" className="flex flex-col items-center gap-0.5 flex-1" data-testid="tab-contact">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${isActive("/contact") ? "bg-black" : ""}`}>
            <Mail className={`w-5 h-5 ${isActive("/contact") ? "text-white" : "text-slate-400"}`} />
          </div>
          <span className={`text-[11px] font-medium ${isActive("/contact") ? "text-black" : "text-slate-400"}`}>Contact</span>
        </Link>

      </div>
    </>
  );
}